import {
  all,
  call,
  delay,
  extractErrorMessage,
  i18n,
  put,
  select,
  showErrorToast,
  showInfoToast,
  showWarningToast,
  takeLatest,
  takeLeading,
  type PayloadAction,
} from '@spcc/core'
import type {
  SubCustomerDetail,
  SubCustomerExportRow,
  SubCustomerOptions,
  SubCustomerRef,
  SubCustomerRequest,
  SubCustomerRequestInput,
  SubCustomerTop,
} from '../domain/types'
import {
  SUB_CUSTOMER_MAX_RANGE_DAYS,
  SUB_CUSTOMER_REFRESH_DEBOUNCE_MS,
  SUB_CUSTOMER_SEARCH_MIN_CHARS,
  SUB_CUSTOMER_T_KEYS as K,
} from '../domain/constants'
import { buildSubCustomerRequest, toOptionsKey } from '../domain/utils/sub-customer-dashboard'
import { countRangeDays } from '../domain/utils/date-range'
import { exportSubCustomerOrders } from '../domain/utils/export-sub-customer-orders'
import { subCustomerDashboardApi as api } from '../infrastructure/api/sub-customer-dashboard-api'
import { subCustomerDashboardActions as A, type ExportRequestedPayload } from './sub-customer-dashboard.slice'
import {
  selectSelectedSubCustomer,
  selectSubCustomerFilters,
  selectSubCustomerGlobalTenant,
  selectSubCustomerOptionsKey,
  selectSubCustomerRequestInput,
} from './sub-customer-dashboard.selectors'

/** Filter clicks come in bursts (multi-select); wait for them to settle before hitting the DB. */
const DEBOUNCED_TYPES: string[] = [A.setFilters.type, A.setDateRange.type]

const currentLocale = () => i18n.language || 'en'
const translate = (key: string, options?: Record<string, unknown>) => i18n.t(key, options) as string
/** The export endpoint answers 400 for an over-long range or more rows than the cap. */
const isNarrowRangeError = (error: unknown) => (error as { status?: number } | null)?.status === 400

function* loadOptions(request: SubCustomerRequest, locale: string, tenantId: string | null) {
  const key = toOptionsKey(request, locale, tenantId)
  const loadedKey: string | null = yield select(selectSubCustomerOptionsKey)
  if (key === loadedKey) return
  try {
    const options: SubCustomerOptions = yield call(api.getOptions, { from: request.from, to: request.to }, locale)
    yield put(A.fetchOptionsSuccess({ key, options }))
  } catch {
    yield put(A.fetchOptionsFailure())
  }
}

function* loadTop(request: SubCustomerRequest, locale: string) {
  try {
    const top: SubCustomerTop = yield call(api.getTop, request, locale)
    yield put(A.fetchTopSuccess(top))
  } catch (error) {
    yield put(A.fetchTopFailure(extractErrorMessage(error)))
  }
}

function* refreshSaga(action: PayloadAction<unknown>) {
  if (DEBOUNCED_TYPES.includes(action.type)) yield delay(SUB_CUSTOMER_REFRESH_DEBOUNCE_MS)
  const input: SubCustomerRequestInput = yield select(selectSubCustomerRequestInput)
  const request = buildSubCustomerRequest(input)
  if (countRangeDays(request.from, request.to) > SUB_CUSTOMER_MAX_RANGE_DAYS) {
    yield put(A.fetchTopFailure(translate(K.RANGE_TOO_LONG, { days: SUB_CUSTOMER_MAX_RANGE_DAYS })))
    return
  }
  const locale = currentLocale()
  yield all([call(loadOptions, request, locale, input.globalTenantId ?? null), call(loadTop, request, locale)])
  const selected: SubCustomerRef | null = yield select(selectSelectedSubCustomer)
  if (selected) yield put(A.fetchDetail())
}

function* detailSaga() {
  const selected: SubCustomerRef | null = yield select(selectSelectedSubCustomer)
  if (!selected) return
  const input: SubCustomerRequestInput = yield select(selectSubCustomerRequestInput)
  try {
    const detail: SubCustomerDetail = yield call(api.getDetail, buildSubCustomerRequest(input, selected.id), currentLocale())
    yield put(A.fetchDetailSuccess({ id: selected.id, detail }))
  } catch (error) {
    yield put(A.fetchDetailFailure())
    yield call(showErrorToast, translate(K.ERROR.TITLE), extractErrorMessage(error))
  }
}

function* searchSaga(action: PayloadAction<string>) {
  const query = action.payload.trim()
  if (query.length < SUB_CUSTOMER_SEARCH_MIN_CHARS) return
  yield delay(SUB_CUSTOMER_REFRESH_DEBOUNCE_MS)
  const tenant: { id: string } | null = yield select(selectSubCustomerGlobalTenant)
  const filters: SubCustomerRequestInput['filters'] = yield select(selectSubCustomerFilters)
  try {
    const results: SubCustomerRef[] = yield call(api.search, query, tenant ? undefined : filters.customerIds)
    yield put(A.searchSuccess(results))
  } catch {
    yield put(A.searchFailure())
  }
}

function* exportSaga(action: PayloadAction<ExportRequestedPayload>) {
  const { from, to, audience, subCustomerId } = action.payload
  const input: SubCustomerRequestInput = yield select(selectSubCustomerRequestInput)
  const request = { ...buildSubCustomerRequest(input, subCustomerId), from, to }
  try {
    const rows: SubCustomerExportRow[] = yield call(api.getExportRows, request, currentLocale())
    if (rows.length > 0) yield call(exportSubCustomerOrders, { rows, audience, from, to, t: translate })
    else yield call(showInfoToast, translate(K.EXPORT.EMPTY))
    yield put(A.exportFinished({ success: rows.length > 0 }))
  } catch (error) {
    if (isNarrowRangeError(error)) yield call(showWarningToast, translate(K.EXPORT.NARROW_RANGE))
    else yield call(showErrorToast, translate(K.EXPORT.FAILED), extractErrorMessage(error))
    yield put(A.exportFinished({ success: false }))
  }
}

export function* subCustomerDashboardSaga() {
  yield all([
    takeLatest([A.init.type, A.refresh.type, A.setDateRange.type, A.setFilters.type], refreshSaga),
    takeLatest([A.selectSubCustomer.type, A.fetchDetail.type], detailSaga),
    takeLatest(A.searchQueryChanged.type, searchSaga),
    takeLeading(A.exportRequested.type, exportSaga),
  ])
}
